import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Counts up from 0 to `end` once, on mount / when `end` first becomes a number.
const useCountUp = (end, duration = 900) => {
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (typeof end !== "number" || Number.isNaN(end)) return;
    if (started.current) {
      setValue(end);
      return;
    }
    started.current = true;

    if (prefersReducedMotion()) {
      setValue(end);
      return;
    }

    const start = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out-cubic
      setValue(Math.round(end * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [end, duration]);

  return value;
};

const StatBox = ({
  title,
  value,
  caption,
  icon,
  accent = "#a370d1",
  format,
  loading,
}) => {
  const theme = useTheme();
  const numericValue = typeof value === "number" ? value : Number(value);
  const isNumeric = !Number.isNaN(numericValue) && value !== null && value !== undefined;
  const animated = useCountUp(isNumeric ? numericValue : 0);

  const display = loading
    ? null
    : !isNumeric
    ? value ?? "—"
    : format === "currency"
    ? `NRs ${animated.toLocaleString()}`
    : animated.toLocaleString();

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "0.85rem",
        p: "1.25rem 1.35rem",
        borderRadius: "0.85rem",
        backgroundColor: theme.palette.background.alt,
        border: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: `${accent}55`,
          boxShadow: `0 12px 24px -12px ${accent}66`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "100%",
          backgroundColor: accent,
          opacity: 0.85,
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{
            color: theme.palette.secondary[200],
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: "0.65rem",
            backgroundColor: `${accent}22`,
            color: accent,
            flexShrink: 0,
            "& svg": { fontSize: "20px" },
          }}
        >
          {icon}
        </Box>
      </Box>

      <Typography
        sx={{
          color: theme.palette.secondary[100],
          fontSize: "1.9rem",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.15,
          minHeight: "2.2rem",
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "60%",
              height: "1.4rem",
              borderRadius: "0.3rem",
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          />
        ) : (
          display
        )}
      </Typography>

      {caption && (
        <Typography
          sx={{
            color: theme.palette.secondary[200],
            fontSize: "0.75rem",
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
};

export default StatBox;
